import { expiryMeta } from '@/lib/expiry';

/**
 * When the shelf empties: how many batches fall due in each window from today
 * out to 90 days, plus the two ends — already expired, and everything beyond.
 *
 * Drawn as plain divs rather than a charting library: seven bars against a
 * single axis need a flexbox, not a dependency, and this way the bars inherit
 * the app's own expiry ramp instead of a chart theme's palette.
 */

// Each bucket answers to a state on the ramp, so the chart and the rows beneath
// it are coloured by the same rule.
const BUCKET_STATE = {
  expired: 'expired',
  d0_7: 'critical',
  d8_15: 'warning',
  d16_30: 'warning',
  d31_60: 'watch',
  d61_90: 'watch',
  beyond: 'fresh',
};

export default function ExpiryTimeline({ timeline = [], onSelect = null }) {
  const max = Math.max(1, ...timeline.map((b) => b.count));

  return (
    <div className="flex h-full min-h-[14rem] items-end gap-2 sm:gap-3">
      {timeline.map((bucket) => {
        const meta = expiryMeta(BUCKET_STATE[bucket.key] || 'unknown');
        // A non-zero bar never renders as a sliver: the floor keeps "1 of 400"
        // visible, which is the whole point of showing the bucket at all.
        const height = bucket.count === 0 ? 2 : Math.max(8, Math.round((bucket.count / max) * 100));
        const Wrapper = onSelect ? 'button' : 'div';

        return (
          <Wrapper
            key={bucket.key}
            onClick={onSelect ? () => onSelect(bucket) : undefined}
            className={`group flex h-full min-w-0 flex-1 flex-col justify-end gap-2 ${
              onSelect ? 'cursor-pointer' : ''
            }`}
            title={`${bucket.label}: ${bucket.count} product(s)`}
          >
            <div className="nums text-center text-sm font-bold" style={{ color: meta.fg }}>
              {bucket.count}
            </div>
            <div
              className="w-full rounded-t-lg transition-all duration-500 ease-out group-hover:opacity-80"
              style={{
                height: `${height}%`,
                background: bucket.count === 0 ? 'var(--app-border)' : meta.solid,
              }}
            />
            <div className="text-center text-[10px] font-medium leading-tight text-[var(--app-muted)] sm:text-[11px]">
              {bucket.label}
            </div>
          </Wrapper>
        );
      })}
    </div>
  );
}
